import { html, list } from "wirejs-dom/v2";

import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import json from 'highlight.js/lib/languages/json';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('xml', xml); // covers html, svg, etc.
hljs.registerLanguage('css', css);
hljs.registerLanguage('json', json);

type Language =
	| 'javascript'
	| 'typescript'
	| 'html'
	| 'xml'
	| 'svg'
	| 'css'
	| 'json'
;

const dedent = (text: string) => {
	const indendations = text
		.split('\n')
		.filter(line => Boolean(line.trim())) // ignore empty lines
		.map(line => {
			const leadingTabs = line.match(/^(\t+)/);
			return leadingTabs ? leadingTabs[1].length : 0
		});

	// smallest indentation is the indendation that matters!
	const indentCount = Math.min(...indendations);
	const indentText = Array(indentCount).fill('\t').join('');
	const indentMatch = new RegExp(`^${indentText}`);

	return text.split('\n').map(line => line.replace(indentMatch, '')).join('\n');
}

const executeCode = (code: string) => {
	return html`<div>
		<b>Output:</b>
		<pre class='code-block'>${list('output')}</pre>
	</div>`.extend(self => ({
		log: (line: string) => {
			self.data.output.push(line);
		},
		error: (line: string) => {
			self.data.output.push(`<span style='color: darkred'>🛑 ${line}</span>`);
		},
		clear: () => {
			self.data.output.splice(0, self.data.output.length);
		}
	})).onadd(self => {
		// available to eval'd code.
		const console = self;
		try {
			eval(code);
		} catch (e: any) {
			self.error(e);
		}
	});
}

export const CodeBlock = <L extends Language>(data: {
	code: string,
	language: L,
	execute?: L extends 'javascript' ? boolean : (false | undefined),
}) => {
	const header = data.execute ? '<b>Code:</b>' : '';
	let output = data.execute ? executeCode(data.code) : '';
	return html`<div>
		${header}
		<pre class='code-block'>${hljs.highlight(
			dedent(data.code),
			{ language: data.language },
		).value.trim()}</pre>
		${output}
	</div>`;
}